/**
 * DnsManager — listado de dominios + ficha a pantalla completa.
 *
 * QUÉ HACE
 * ---------------------------------------------------------------------------
 * · Lista los dominios de la empresa con estado, proveedor, caducidad (con los
 *   días que quedan) e nº de registros. La fila se marca en naranja si caduca
 *   en ≤30 días y en rojo si ya ha vencido.
 * · Al pulsar una fila se abre `DnsDomainForm` (ficha real, no modal): ahí se
 *   edita, se ven los registros agrupados por tipo, se sincroniza con OVH y se
 *   da de alta un dominio nuevo (con disponibilidad, precio y pedido real).
 * · «Comprobar dominio» resuelve de verdad el dominio (A/AAAA/CNAME/MX/NS/TXT/
 *   SOA, PTR inverso) y avisa de lo típico.
 * · «Importar de OVH» (superadmin) trae las zonas que ya existen en la cuenta y
 *   las reparte entre empresas. Los proveedores viven en `/admin/dns`.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  CheckBadgeIcon,
  CloudArrowDownIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';
import DnsDomainForm from './DnsDomainForm';
import { usuarioActual } from './feedbackConfig';

const ESTADOS = [
  { value: 'active', label: 'Activo' },
  { value: 'expired', label: 'Caducado' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'unknown', label: 'Desconocido' },
  { value: 'transferred', label: 'Transferido' },
];

/** Días que quedan hasta la fecha (negativo = vencido) y su tono. */
function vencimiento(fecha) {
  if (!fecha) return null;
  const dias = Math.ceil((new Date(`${fecha}T23:59:59`) - Date.now()) / 86400000);
  return { dias, tono: dias < 0 ? 'bad' : dias <= 30 ? 'warn' : 'ok' };
}

export default function DnsManager({ companyId }) {
  const superadmin = useMemo(() => usuarioActual().superadmin, []);
  const [dominios, setDominios] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [aviso, setAviso] = useState('');

  /** `{ domain, mode }` — null = listado; si no, ficha (create | view). */
  const [ficha, setFicha] = useState(null);

  // Importación masiva de zonas del proveedor (solo superadmin).
  const [importOpen, setImportOpen] = useState(false);
  const [importProvider, setImportProvider] = useState('');
  const [importZones, setImportZones] = useState([]);
  const [importSync, setImportSync] = useState(true);
  const [importBusy, setImportBusy] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [empresaMasiva, setEmpresaMasiva] = useState('');

  // Comprobaciones DNS.
  const [checkOpen, setCheckOpen] = useState(false);
  const [checkDomain, setCheckDomain] = useState('');
  const [checkResult, setCheckResult] = useState(null);
  const [checkBusy, setCheckBusy] = useState(false);
  const [checkError, setCheckError] = useState('');

  const cargar = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.getDnsDomains(companyId);
      setDominios(res.data || []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los dominios');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (!superadmin) return;
    api.getDnsProviders()
      .then((res) => setProveedores(res.data || []))
      .catch(() => setProveedores([]));
  }, [superadmin]);

  const mostrarAviso = (texto) => {
    setAviso(texto);
    setTimeout(() => setAviso(''), 3000);
  };

  const proveedoresOvh = useMemo(
    () => proveedores.filter((p) => p.provider === 'ovh' && p.is_active),
    [proveedores],
  );

  const leerZonas = useCallback(async (providerId) => {
    if (!providerId) return;
    setImportLoading(true);
    setImportError('');
    setImportResult(null);
    try {
      const res = await api.getDnsProviderZones(providerId);
      const porDefecto = companyId && companyId !== 'all' ? String(companyId) : '';
      setImportZones((res.data || []).map((row) => ({
        zone: row.zone,
        existing: row.domains || [],
        // Por defecto solo las que aún no están en la CMDB: las demás pueden
        // ser de otro cliente y no queremos duplicarlas sin querer.
        selected: (row.domains || []).length === 0,
        company_id: porDefecto,
      })));
    } catch (err) {
      setImportZones([]);
      setImportError(err.message || 'No se pudieron leer las zonas de OVH');
    } finally {
      setImportLoading(false);
    }
  }, [companyId]);

  const abrirImportacion = async () => {
    const primera = proveedoresOvh[0];
    setImportProvider(primera ? String(primera.id) : '');
    setImportZones([]);
    setImportResult(null);
    setImportError('');
    setImportSync(true);
    setEmpresaMasiva('');
    setImportOpen(true);
    try {
      setEmpresas(await api.getAuthorizedCompanies());
    } catch {
      setEmpresas([]);
    }
    if (primera) leerZonas(String(primera.id));
  };

  const importarZonas = async () => {
    const elegidas = importZones.filter((row) => row.selected);
    if (!elegidas.length) {
      setImportError('Marca al menos una zona');
      return;
    }
    const sinEmpresa = elegidas.filter((row) => !row.company_id);
    if (sinEmpresa.length) {
      setImportError(`Asigna una empresa a: ${sinEmpresa.map((row) => row.zone).join(', ')}`);
      return;
    }
    setImportBusy(true);
    setImportError('');
    try {
      const res = await api.importDnsDomains({
        provider_id: Number(importProvider),
        sync: importSync,
        items: elegidas.map((row) => ({ zone: row.zone, company_id: Number(row.company_id) })),
      });
      await cargar();
      await leerZonas(importProvider);
      setImportResult(res);
      mostrarAviso(`Importación: ${res.created} creados · ${res.skipped} ya existían`);
    } catch (err) {
      setImportError(err.message || 'No se pudieron importar las zonas');
    } finally {
      setImportBusy(false);
    }
  };

  const comprobar = async (modo) => {
    if (!checkDomain.trim()) return;
    setCheckBusy(true);
    setCheckError('');
    setCheckResult(null);
    try {
      const data = modo === 'availability'
        ? await api.checkDnsAvailability(checkDomain, companyId)
        : await api.checkDnsDomain(checkDomain, companyId);
      setCheckResult({ modo, data });
    } catch (err) {
      setCheckError(err.message || 'No se pudo comprobar el dominio');
    } finally {
      setCheckBusy(false);
    }
  };

  const filtrados = useMemo(() => {
    const q = search.trim().toLowerCase();
    return dominios.filter((dominio) => {
      const coincide = !q
        || (dominio.name || '').toLowerCase().includes(q)
        || (dominio.registrar || '').toLowerCase().includes(q)
        || (dominio.provider_name || '').toLowerCase().includes(q);
      const estado = !filtroEstado || dominio.status === filtroEstado;
      return coincide && estado;
    });
  }, [dominios, search, filtroEstado]);

  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'name',
        label: 'Dominio',
        sortable: true,
        render: (d) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <GlobeAltIcon style={{ width: 15, height: 15, color: 'var(--accent-l)' }} />
            <strong>{d.name}</strong>
          </span>
        ),
      },
      {
        key: 'status',
        label: 'Estado',
        sortable: true,
        render: (d) => (
          <span className={`list-badge ${
            d.status === 'active' ? 'list-badge--on'
              : d.status === 'expired' ? 'list-badge--off'
                : d.status === 'pending' ? 'list-badge--warn' : 'list-badge--muted'
          }`}>
            {d.status_label || d.status}
          </span>
        ),
      },
      { key: 'provider_name', label: 'Proveedor', sortable: true, render: (d) => d.provider_name || dash },
      { key: 'registrar', label: 'Registrador', sortable: true, render: (d) => d.registrar || dash },
      {
        key: 'expires_at',
        label: 'Caduca',
        sortable: true,
        render: (d) => {
          const v = vencimiento(d.expires_at);
          if (!v) return dash;
          const clase = v.tono === 'bad' ? 'list-badge--off' : v.tono === 'warn' ? 'list-badge--warn' : 'list-badge--muted';
          return (
            <span
              className={`list-badge ${clase}`}
              title={d.auto_renew ? 'Renovación automática' : 'Renovación manual'}
            >
              {d.expires_at} · {v.dias < 0 ? `vencido ${Math.abs(v.dias)} d` : `${v.dias} d`}
            </span>
          );
        },
      },
      {
        key: 'record_count',
        label: 'Registros',
        sortable: true,
        render: (d) => <span className="list-badge list-badge--accent">{d.record_count || 0}</span>,
      },
      {
        key: 'last_sync_at',
        label: 'Última sincronización',
        sortable: true,
        render: (d) => (d.last_sync_at
          ? <span className="list-hint" title={d.last_sync_message || ''}>{new Date(d.last_sync_at).toLocaleString()}</span>
          : dash),
      },
    ];
  }, []);

  const exportRows = useMemo(
    () => filtrados.map((d) => {
      const v = vencimiento(d.expires_at);
      return {
        Dominio: d.name,
        Estado: d.status_label || d.status,
        Proveedor: d.provider_name || '',
        Registrador: d.registrar || '',
        Caduca: d.expires_at || '',
        'Días restantes': v ? v.dias : '',
        'Renovación automática': d.auto_renew ? 'Sí' : 'No',
        Registros: d.record_count || 0,
      };
    }),
    [filtrados],
  );

  // La fila se tiñe según la caducidad: naranja si queda poco, roja si venció.
  const rowClassName = (d) => {
    const v = vencimiento(d.expires_at);
    if (!v) return '';
    if (v.dias < 0) return 'list-row-danger';
    if (v.dias <= 30) return 'list-row-warn';
    return '';
  };

  if (ficha) {
    return (
      <DnsDomainForm
        domain={ficha.domain}
        mode={ficha.mode}
        companyId={companyId}
        providers={proveedores}
        superadmin={superadmin}
        siblings={filtrados}
        onClose={() => setFicha(null)}
        onSaved={cargar}
        onDeleted={() => { setFicha(null); cargar(); }}
        onNavigate={(fila) => setFicha({ domain: fila, mode: 'view' })}
      />
    );
  }

  const importSeleccionadas = importZones.filter((row) => row.selected).length;

  return (
    <>
      <ListPage
        icon={GlobeAltIcon}
        title="DNS"
        subtitle="Dominios de Internet, registros y caducidades. Con proveedor, la zona se lee y escribe en OVH."
        companyId={companyId}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar dominio, registrador o proveedor…"
        filters={[
          {
            key: 'status',
            label: 'Estado',
            allLabel: 'Todos los estados',
            value: filtroEstado,
            onChange: setFiltroEstado,
            options: ESTADOS,
          },
        ]}
        headerActions={(
          <>
            <button
              type="button"
              className="list-btn-secondary"
              onClick={() => { setCheckOpen(true); setCheckResult(null); setCheckError(''); }}
            >
              <MagnifyingGlassIcon style={{ width: 15, height: 15 }} /> Comprobar dominio
            </button>
            {superadmin && (
              <button
                type="button"
                className="list-btn-secondary"
                onClick={abrirImportacion}
                title="Traer a la CMDB las zonas que ya existen en OVH"
              >
                <CloudArrowDownIcon style={{ width: 15, height: 15 }} /> Importar de OVH
              </button>
            )}
            {superadmin && (
              <a className="list-btn-secondary" href="/admin/dns" title="Credenciales de proveedores (solo superadmin)">
                <ServerStackIcon style={{ width: 15, height: 15 }} /> Proveedores
              </a>
            )}
          </>
        )}
        columns={columns}
        columnStorageKey="cmdb_cols_dns"
        listKey="dns"
        rows={filtrados}
        rowClassName={rowClassName}
        onRowClick={(d) => setFicha({ domain: d, mode: 'view' })}
        exportRows={exportRows}
        exportFileBase="dominios-dns"
        loading={loading && dominios.length === 0}
        error={error}
        onCreate={() => setFicha({ domain: null, mode: 'create' })}
        createLabel="Nuevo dominio"
        createDisabled={!companyId || companyId === 'all'}
        createTitle={companyId && companyId !== 'all'
          ? 'Dar de alta un dominio'
          : 'Selecciona una empresa en la barra superior para dar de alta dominios'}
        empty={{
          icon: '🌐',
          title: dominios.length === 0 ? 'Todavía no hay dominios' : 'Ningún dominio coincide',
          subtitle: dominios.length === 0
            ? 'Da de alta el dominio (con disponibilidad y coste) o importa las zonas que ya tienes en OVH.'
            : undefined,
          action: dominios.length === 0 ? (
            <button
              type="button"
              className="list-btn-primary"
              onClick={() => setFicha({ domain: null, mode: 'create' })}
              disabled={!companyId || companyId === 'all'}
            >
              <PlusIcon style={{ width: 14, height: 14 }} /> Nuevo dominio
            </button>
          ) : undefined,
        }}
      />

      {aviso && (
        <div className="list-badge list-badge--accent" style={{ position: 'fixed', bottom: 18, right: 18, zIndex: 1100 }}>
          {aviso}
        </div>
      )}

      {/* ══ Importar zonas de OVH ══ */}
      {importOpen && (
        <Dialog
          title="Importar zonas de OVH"
          icon={<CloudArrowDownIcon style={{ width: 18, height: 18 }} />}
          maxWidth={880}
          busy={importBusy || importLoading}
          onClose={() => setImportOpen(false)}
          footer={(
            <>
              {importResult && (
                <span className="list-hint" style={{ marginRight: 'auto' }}>
                  {importResult.created} creados
                  {importResult.skipped ? ` · ${importResult.skipped} ya existían` : ''}
                  {importResult.synced ? ` · ${importResult.synced} sincronizados` : ''}
                  {importResult.failed ? ` · ${importResult.failed} con error` : ''}
                </span>
              )}
              <button type="button" className="list-btn-secondary" onClick={() => setImportOpen(false)} disabled={importBusy}>
                Cerrar
              </button>
              <button
                type="button"
                className="list-btn-primary"
                onClick={importarZonas}
                disabled={importBusy || importLoading || importSeleccionadas === 0}
              >
                {importBusy ? 'Importando…' : `Importar ${importSeleccionadas}`}
              </button>
            </>
          )}
        >
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label className="list-hint">
              Proveedor
              <select
                className="list-select"
                style={{ minWidth: 210, marginTop: 3 }}
                value={importProvider}
                onChange={(e) => { setImportProvider(e.target.value); leerZonas(e.target.value); }}
              >
                {proveedoresOvh.length === 0 && <option value="">Sin proveedor OVH activo</option>}
                {proveedoresOvh.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.endpoint}</option>)}
              </select>
            </label>
            <button
              type="button"
              className="list-btn-secondary"
              onClick={() => leerZonas(importProvider)}
              disabled={importLoading || !importProvider}
            >
              <ArrowPathIcon style={{ width: 15, height: 15 }} /> {importLoading ? 'Leyendo…' : 'Recargar zonas'}
            </button>
            <label className="list-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 6 }}>
              <input type="checkbox" checked={importSync} onChange={(e) => setImportSync(e.target.checked)} />
              Traer los registros de cada zona
            </label>
          </div>

          <p className="list-hint">
            Las zonas salen de la cuenta de OVH del proveedor. Asigna cada una a su empresa y marca
            solo las que quieras dar de alta: si esa empresa ya la tiene, se omite.
          </p>

          {proveedoresOvh.length === 0 && (
            <p className="list-hint">
              No hay ningún proveedor OVH activo. Créalo en <a href="/admin/dns">Proveedores DNS</a>.
            </p>
          )}

          {importZones.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', margin: '4px 0 8px' }}>
              <span className="list-hint">Asignar a todas:</span>
              <select
                className="list-select"
                style={{ minWidth: 200 }}
                value={empresaMasiva}
                onChange={(e) => setEmpresaMasiva(e.target.value)}
              >
                <option value="">— Elige empresa —</option>
                {empresas.map((empresa) => <option key={empresa.id} value={empresa.id}>{empresa.name}</option>)}
              </select>
              <button
                type="button"
                className="list-btn-secondary"
                disabled={!empresaMasiva}
                onClick={() => setImportZones((rows) => rows.map((row) => ({ ...row, company_id: empresaMasiva })))}
              >
                Aplicar
              </button>
              <button
                type="button"
                className="list-btn-secondary"
                onClick={() => setImportZones((rows) => rows.map((row) => ({ ...row, selected: true })))}
              >
                Marcar todas
              </button>
              <button
                type="button"
                className="list-btn-secondary"
                onClick={() => setImportZones((rows) => rows.map((row) => ({ ...row, selected: false })))}
              >
                Ninguna
              </button>
            </div>
          )}

          {importError && <p style={{ color: 'var(--danger, #ef4444)' }}>{importError}</p>}

          {!importLoading && importZones.length === 0 && !importError && proveedoresOvh.length > 0 && (
            <p className="list-hint" style={{ marginTop: 10 }}>
              {importProvider ? 'No hay zonas en esa cuenta de OVH.' : 'Elige un proveedor para leer sus zonas.'}
            </p>
          )}

          {importZones.length > 0 && (
            <div style={{ maxHeight: 340, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <th style={{ width: 28, padding: '6px 8px' }} />
                    <th style={{ textAlign: 'left', padding: '6px 8px' }}>Zona</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px' }}>En la CMDB</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', minWidth: 210 }}>Empresa</th>
                  </tr>
                </thead>
                <tbody>
                  {importZones.map((row) => (
                    <tr key={row.zone} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '5px 8px' }}>
                        <input
                          type="checkbox"
                          checked={row.selected}
                          onChange={(e) => setImportZones((rows) => rows.map((item) => (
                            item.zone === row.zone ? { ...item, selected: e.target.checked } : item
                          )))}
                        />
                      </td>
                      <td style={{ padding: '5px 8px' }}><strong>{row.zone}</strong></td>
                      <td style={{ padding: '5px 8px' }}>
                        {row.existing.length === 0 ? (
                          <span className="list-badge list-badge--on">Nueva</span>
                        ) : (
                          <span className="list-badge list-badge--warn" title="Ya está dada de alta">
                            Ya en {row.existing.map((d) => d.company_name || `empresa ${d.company_id}`).join(', ')}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '5px 8px' }}>
                        <select
                          className="list-select"
                          style={{ width: '100%' }}
                          value={row.company_id}
                          onChange={(e) => setImportZones((rows) => rows.map((item) => (
                            item.zone === row.zone ? { ...item, company_id: e.target.value } : item
                          )))}
                        >
                          <option value="">— Elige empresa —</option>
                          {empresas.map((empresa) => <option key={empresa.id} value={empresa.id}>{empresa.name}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {importResult?.data?.some((row) => row.status === 'created_no_sync' || row.status === 'error') && (
            <ul className="list-hint" style={{ margin: '8px 0 0 18px' }}>
              {importResult.data
                .filter((row) => row.status === 'created_no_sync' || row.status === 'error')
                .map((row) => <li key={`${row.zone}-${row.status}`}>{row.zone}: {row.message}</li>)}
            </ul>
          )}
        </Dialog>
      )}

      {/* ══ Comprobaciones ══ */}
      {checkOpen && (
        <Dialog
          title="Comprobar un dominio"
          icon={<MagnifyingGlassIcon style={{ width: 18, height: 18 }} />}
          maxWidth={720}
          busy={checkBusy}
          onClose={() => setCheckOpen(false)}
          footer={<button type="button" className="list-btn-secondary" onClick={() => setCheckOpen(false)}>Cerrar</button>}
        >
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              className="list-input"
              style={{ flex: '1 1 240px' }}
              placeholder="ejemplo.com"
              value={checkDomain}
              onChange={(e) => setCheckDomain(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') comprobar('full'); }}
            />
            <button type="button" className="list-btn-secondary" onClick={() => comprobar('availability')} disabled={checkBusy}>
              ¿Está libre?
            </button>
            <button type="button" className="list-btn-primary" onClick={() => comprobar('full')} disabled={checkBusy}>
              {checkBusy ? 'Comprobando…' : 'Analizar registros'}
            </button>
          </div>

          {checkError && <p style={{ color: 'var(--danger, #ef4444)', marginTop: 10 }}>{checkError}</p>}

          {checkResult?.modo === 'availability' && (
            <div style={{ marginTop: 12 }}>
              <span className={`list-badge ${
                checkResult.data.available === true ? 'list-badge--on'
                  : checkResult.data.available === false ? 'list-badge--off' : 'list-badge--warn'
              }`}>
                {checkResult.data.message}
              </span>
              {checkResult.data.registrar && (
                <p className="list-hint" style={{ marginBottom: 0 }}>Registrador: {checkResult.data.registrar}</p>
              )}
              {checkResult.data.expires_at && (
                <p className="list-hint" style={{ marginBottom: 0 }}>Caduca: {String(checkResult.data.expires_at).slice(0, 10)}</p>
              )}
              {checkResult.data.nservers?.length > 0 && (
                <p className="list-hint" style={{ marginBottom: 0 }}>NS: {checkResult.data.nservers.join(', ')}</p>
              )}
            </div>
          )}

          {checkResult?.modo === 'full' && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                {checkResult.data.ok
                  ? <><CheckBadgeIcon style={{ width: 18, height: 18, color: 'var(--success, #22c55e)' }} /><strong>Sin problemas detectados</strong></>
                  : <><ExclamationTriangleIcon style={{ width: 18, height: 18, color: 'var(--warn, #f59e0b)' }} /><strong>Avisos</strong></>}
              </div>
              {checkResult.data.issues?.length > 0 && (
                <ul style={{ margin: '0 0 10px 18px', padding: 0, fontSize: '0.82rem' }}>
                  {checkResult.data.issues.map((issue) => <li key={issue}>{issue}</li>)}
                </ul>
              )}
              {Object.entries(checkResult.data.records || {}).map(([tipo, valor]) => (
                <div key={tipo} style={{ padding: '5px 0', borderTop: '1px solid var(--border)', fontSize: '0.8rem' }}>
                  <strong>{tipo}</strong>{' '}
                  {valor.values?.length
                    ? <span className="list-code">{valor.values.join(' · ')}</span>
                    : <span className="list-hint">{valor.error || 'sin respuesta'}</span>}
                </div>
              ))}
              {checkResult.data.reverse?.length > 0 && (
                <p className="list-hint" style={{ marginTop: 8 }}>PTR: {checkResult.data.reverse.join(', ')}</p>
              )}
            </div>
          )}
        </Dialog>
      )}
    </>
  );
}
